import React, { useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import axios from 'axios';
import { Box, Modal, Button, Switch, FormControlLabel } from '@mui/material';
import { DataGrid } from '@mui/x-data-grid';
import Spinner from '../../layout/Spinner';
import { authRoute, localToken } from '../../utils/constants';
import { generalSet } from '../../store/generalSlice';

const SqlValues = () => {
  const dispatch = useDispatch();
  const { sqlValues } = useSelector((state) => state.general);

  const [loading, setLoading] = useState(false);
  const [searchValue, setSearchValue] = useState('');

  const [openEditor, setOpenEditor] = useState(false);
  const [openDeleteModal, setOpenDeleteModal] = useState(false);
  const [openErrorModal, setOpenErrorModal] = useState(false);

  const [selectedSqlValue, setSelectedSqlValue] = useState(null);
  const [editorMode, setEditorMode] = useState('create');

  const [formData, setFormData] = useState({
    name: '',
    value: '',
    isActive: true,
  });

  const [errorMessage, setErrorMessage] = useState('Something went wrong. Please try again.');

  const headers = useMemo(
    () => ({
      Authorization: `Baerer ${localToken}`,
    }),
    [],
  );

  const handleApiError = (error, defaultMessage) => {
    const message =
      error?.response?.data?.message ||
      (typeof error?.response?.data === 'string' ? error.response.data : null) ||
      defaultMessage;
    setErrorMessage(message);
    setOpenErrorModal(true);
  };

  const fetchSqlValues = async () => {
    try {
      setLoading(true);
      const response = await axios.post(`${authRoute}/sqlValues/getSqlValues`, {}, { headers });
      const responseData = response?.data;

      if (!responseData?.success) {
        setErrorMessage(responseData?.message || 'Failed to fetch SQL values');
        setOpenErrorModal(true);
        return;
      }

      dispatch(
        generalSet({
          reduxField: 'sqlValues',
          value: Array.isArray(responseData?.data) ? responseData.data : [],
        }),
      );
    } catch (error) {
      handleApiError(error, 'Failed to fetch SQL values');
    } finally {
      setLoading(false);
    }
  };

  const resetForm = () => {
    setFormData({
      name: '',
      value: '',
      isActive: true,
    });
  };

  const handleOpenCreate = () => {
    setEditorMode('create');
    setSelectedSqlValue(null);
    resetForm();
    setOpenEditor(true);
  };

  const handleOpenEdit = (sqlValue) => {
    setEditorMode('edit');
    setSelectedSqlValue(sqlValue);
    setFormData({
      name: sqlValue?.name || '',
      value: sqlValue?.value || '',
      isActive: !!sqlValue?.isActive,
    });
    setOpenEditor(true);
  };

  const handleCloseEditor = () => {
    setOpenEditor(false);
    setSelectedSqlValue(null);
    resetForm();
  };

  const handleOpenDelete = (sqlValue) => {
    setSelectedSqlValue(sqlValue);
    setOpenDeleteModal(true);
  };

  const handleCloseDelete = () => {
    setOpenDeleteModal(false);
    setSelectedSqlValue(null);
  };

  const handleSave = async () => {
    if (!formData.name?.trim()) {
      setErrorMessage('Name is required');
      setOpenErrorModal(true);
      return;
    }

    try {
      setLoading(true);

      if (editorMode === 'create') {
        const response = await axios.post(`${authRoute}/sqlValues/createSqlValue`, formData, { headers });
        if (!response?.data?.success) {
          setErrorMessage(response?.data?.message || 'Failed to create SQL value');
          setOpenErrorModal(true);
          return;
        }
      }

      if (editorMode === 'edit' && selectedSqlValue?._id) {
        const response = await axios.post(
          `${authRoute}/sqlValues/updateSqlValue`,
          {
            id: selectedSqlValue._id,
            data: formData,
          },
          { headers },
        );

        if (!response?.data?.success) {
          setErrorMessage(response?.data?.message || 'Failed to update SQL value');
          setOpenErrorModal(true);
          return;
        }
      }

      await fetchSqlValues();
      handleCloseEditor();
    } catch (error) {
      handleApiError(error, editorMode === 'create' ? 'Failed to create SQL value' : 'Failed to update SQL value');
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async () => {
    if (!selectedSqlValue?._id) {
      return;
    }

    try {
      setLoading(true);
      const response = await axios.post(
        `${authRoute}/sqlValues/deleteSqlValue`,
        { id: selectedSqlValue._id },
        { headers },
      );

      if (!response?.data?.success) {
        setErrorMessage(response?.data?.message || 'Failed to delete SQL value');
        setOpenErrorModal(true);
        return;
      }

      await fetchSqlValues();
      handleCloseDelete();
    } catch (error) {
      handleApiError(error, 'Failed to delete SQL value');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchSqlValues();
  }, []);

  const filteredSqlValues = useMemo(() => {
    if (!Array.isArray(sqlValues)) {
      return [];
    }

    const search = searchValue.trim().toLowerCase();
    if (!search) {
      return sqlValues;
    }

    return sqlValues.filter((item) => {
      const name = (item?.name || '').toLowerCase();
      const value = (item?.value || '').toLowerCase();
      const isActive = item?.isActive ? 'active' : 'inactive';
      return (
        name.includes(search) || value.includes(search) || isActive.includes(search) || item?._id?.includes(search)
      );
    });
  }, [sqlValues, searchValue]);

  const columns = [
    {
      field: '_id',
      headerName: 'ID',
      flex: 1,
      renderCell: (params) => (
        <span title={params.value || ''}>
          <button
            title='Copy ID'
            className='btn btn-sm btn-outline-secondary border-0 me-2'
            onClick={() => navigator.clipboard.writeText(params.value || '')}
          >
            <i className='fas fa-copy'></i>
          </button>{' '}
          {params.value || '-'}
        </span>
      ),
    },
    {
      field: 'name',
      headerName: 'Name',
      width: 260,
      flex: 1,
    },
    {
      field: 'value',
      headerName: 'Value',
      width: 420,
      flex: 2,
      renderCell: (params) => <span title={params.value || ''}>{params.value || '-'}</span>,
    },
    {
      field: 'isActive',
      headerName: 'Status',
      width: 70,
      renderCell: (params) =>
        params.row.isActive ? (
          <span className='badge bg-success'>Active</span>
        ) : (
          <span className='badge bg-secondary'>Inactive</span>
        ),
    },
    {
      field: 'actions',
      headerName: '',
      width: 120,
      sortable: false,
      renderCell: (params) => (
        <div style={{ display: 'flex', marginTop: '5%' }}>
          <button className='btn btn-sm btn-outline-primary me-2' onClick={() => handleOpenEdit(params.row)}>
            Edit
          </button>
          <button className='btn btn-sm btn-outline-danger' onClick={() => handleOpenDelete(params.row)}>
            Delete
          </button>
        </div>
      ),
    },
  ];

  return (
    <div className='container mt-4 mb-4' data-aos='fade-up' data-aos-delay='200'>
      <div className='row d-flex justify-content-center border-bottom'>
        <h1>SQL Values</h1>
      </div>

      <div className='row d-flex justify-content-center mt-4'>
        <div className='col-lg-10'>
          <div className='card shadow-sm'>
            <div className='card-body'>
              <div className='d-flex flex-wrap justify-content-between align-items-center gap-2 mb-3'>
                <div className='input-group' style={{ maxWidth: '420px' }}>
                  <span className='input-group-text'>
                    <i className='fas fa-search'></i>
                  </span>
                  <input
                    type='text'
                    className='form-control'
                    placeholder='Search by name, value, status, or ID'
                    value={searchValue}
                    onChange={(event) => setSearchValue(event.target.value)}
                  />
                </div>

                <button className='btn btn-outline-dark' onClick={handleOpenCreate}>
                  Create SQL Value
                </button>
              </div>
              <div className='mb-3'>
                <h6>
                  {
                    'Variable names will be wrapped with {{variable.name}}, variable IDs will be wrapped with [[variable._id]]'
                  }
                </h6>
              </div>
              {loading && !Array.isArray(sqlValues) ? (
                <div className='d-flex justify-content-center mt-3 mb-3'>
                  <Spinner />
                </div>
              ) : (
                <Box style={{ height: 580, width: '100%' }}>
                  <DataGrid
                    rows={filteredSqlValues.map((item) => ({ id: item._id, ...item }))}
                    columns={columns}
                    pageSizeOptions={[10, 25, 50]}
                    initialState={{
                      pagination: {
                        paginationModel: { pageSize: 10, page: 0 },
                      },
                    }}
                    disableRowSelectionOnClick
                  />
                </Box>
              )}
            </div>
          </div>
        </div>
      </div>

      <Modal
        open={openEditor}
        onClose={handleCloseEditor}
        style={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}
      >
        <div
          className='modal-content'
          style={{
            width: '560px',
            maxWidth: '95%',
            padding: '20px',
            backgroundColor: 'white',
            borderRadius: '8px',
            outline: 'none',
          }}
        >
          <h4>{editorMode === 'create' ? 'Create SQL Value' : 'Edit SQL Value'}</h4>

          <div className='mt-3'>
            <label className='form-label'>Name</label>
            <input
              type='text'
              className='form-control'
              value={formData.name}
              onChange={(event) => setFormData({ ...formData, name: event.target.value })}
              placeholder='Enter unique name'
            />
          </div>

          <div className='mt-3'>
            <label className='form-label'>Value</label>
            <textarea
              rows={4}
              className='form-control'
              value={formData.value}
              onChange={(event) => setFormData({ ...formData, value: event.target.value })}
              placeholder='Enter SQL value'
            />
          </div>

          <div className='mt-3'>
            <FormControlLabel
              control={
                <Switch
                  checked={formData.isActive}
                  onChange={(event) => setFormData({ ...formData, isActive: event.target.checked })}
                />
              }
              label={formData.isActive ? 'Active' : 'Inactive'}
            />
          </div>

          <div className='d-flex justify-content-end gap-2 mt-4'>
            <Button variant='outlined' onClick={handleCloseEditor}>
              Cancel
            </Button>
            <Button variant='contained' onClick={handleSave}>
              {editorMode === 'create' ? 'Create' : 'Update'}
            </Button>
          </div>
        </div>
      </Modal>

      <Modal
        open={openDeleteModal}
        onClose={handleCloseDelete}
        style={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}
      >
        <div
          className='modal-content'
          style={{
            width: '460px',
            maxWidth: '95%',
            padding: '20px',
            backgroundColor: 'white',
            borderRadius: '8px',
            outline: 'none',
          }}
        >
          <h4>Confirm Deletion</h4>
          <p className='mb-3'>Are you sure you want to delete this SQL value?</p>
          <div className='d-flex justify-content-end gap-2'>
            <Button variant='outlined' onClick={handleCloseDelete}>
              Cancel
            </Button>
            <Button variant='contained' color='error' onClick={handleDelete}>
              Delete
            </Button>
          </div>
        </div>
      </Modal>

      <Modal
        open={openErrorModal}
        onClose={() => setOpenErrorModal(false)}
        style={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}
      >
        <div
          className='modal-content'
          style={{
            width: '520px',
            maxWidth: '95%',
            padding: '20px',
            backgroundColor: 'white',
            borderRadius: '8px',
            outline: 'none',
          }}
        >
          <h4>Something Went Wrong</h4>
          <p className='mb-3'>{errorMessage}</p>
          <div className='d-flex justify-content-end'>
            <Button variant='contained' onClick={() => setOpenErrorModal(false)}>
              Close
            </Button>
          </div>
        </div>
      </Modal>
    </div>
  );
};

export default SqlValues;
